import React from "react";
import { SPECIAL_CATEGORIES } from "../../lib/taxConfig";

const fmt = (n) => Math.abs(Number(n || 0)).toLocaleString("en-MY", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const money = (n, deduction) => {
  const v = Number(n || 0);
  if (deduction || v < 0) return `(${fmt(v)})`;
  return fmt(v);
};

const CG = "grid grid-cols-[1fr_34px_112px_122px]";

const Row = ({ label, note, inner, outer, deduction, indent, section, emphasise, subtotal, innerTop, bold, dataTestid }) => {
  if (section) {
    return (
      <div className={`${CG} pt-3.5 pb-1`}>
        <div className="text-sm font-bold uppercase tracking-widest text-slate-900 col-span-4">{label}</div>
      </div>
    );
  }
  const fmtCell = (v, ded) => (v === null || v === undefined || v === "" ? "" : money(v, ded));
  return (
    <div className={`${CG} items-baseline py-0.5 ${emphasise ? "mt-1" : ""}`}>
      <div className={`text-sm ${indent ? "pl-6 text-slate-600" : "text-slate-800"} ${subtotal || bold ? "font-semibold text-slate-900" : ""} ${emphasise ? "font-bold uppercase tracking-wide text-slate-900" : ""}`}>{label}</div>
      <div className="text-[11px] text-center text-slate-400">{note || ""}</div>
      <div className={`text-sm font-mono tabular-nums text-right text-slate-900 pr-1 ${innerTop ? "border-t border-slate-900 pt-1" : ""}`}>{fmtCell(inner, deduction)}</div>
      <div
        data-testid={dataTestid}
        className={`text-sm font-mono tabular-nums text-right text-slate-900 pr-1 ${subtotal ? "border-t border-slate-900 font-semibold pt-1" : ""} ${emphasise ? "border-t-2 border-b-4 border-double border-slate-900 font-bold py-1" : ""}`}
      >{fmtCell(outer, deduction)}</div>
    </div>
  );
};

const KV = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className="font-semibold text-slate-900 text-right">{v || "\u2014"}</span>
  </div>
);

// The A4 body of the tax computation report (letterhead + computation + notes + special deductions).
// Reused by the on-screen ReportPreview modal and the Company Report document page.
const ReportBody = ({ r }) => {
  if (!r) return null;
  const h = r.header || {};
  const disList = r.disallowables || [];
  const hasNotes = (d) => (d.notes && d.notes.length) || (d.note && String(d.note).trim());
  let n = 0;
  const noteNo = new Map();
  disList.forEach((d) => { if (hasNotes(d)) noteNo.set(d, ++n); });
  const shownDis = disList.filter((d) => Number(d.disallowed || 0) !== 0);
  const otherIncome = r.otherIncome || [];
  const specialDeductions = (r.specialDeductions || []).filter((s) => Number(s.amount || 0) !== 0);
  const otherIncomeSum = otherIncome.reduce((s, o) => s + Number(o.amount || 0), 0);
  const businessIncomeLoss = Number(r.netProfit || 0) - otherIncomeSum;
  const sdTotal = specialDeductions.reduce((s, d) => s + Number(d.amount || 0), 0);

  const sdSchedule = SPECIAL_CATEGORIES.map((cat) => {
    const lines = [];
    disList.forEach((d) => {
      const subs = (d.notes || []).filter((s) => s.treatment === "disallowed" && s.specialCategory === cat.key);
      if (subs.length) subs.forEach((s) => lines.push({ desc: d.description, text: s.text, amount: Number(s.amount || 0) }));
      else if (d.treatment === "disallowed" && d.specialCategory === cat.key && Number(d.disallowed || 0) !== 0)
        lines.push({ desc: d.description, text: d.remark || d.note || "", amount: Number(d.disallowed || 0) });
    });
    return { cat, lines, total: lines.reduce((s, l) => s + l.amount, 0) };
  }).filter((g) => g.lines.length);

  const notedRows = disList.filter(hasNotes);

  return (
    <>
      {/* Letterhead */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 pb-6 border-b-2 border-slate-900 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Company</div>
          <div className="text-xl font-bold uppercase text-slate-900 leading-snug">{h.companyName || "\u2014"}</div>
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-widest text-[#1877c9]">Tax Computation — Form C</div>
        </div>
        <div className="space-y-1.5 md:pt-1">
          <KV k="Tax File No." v={h.taxFileNo} />
          <KV k="Ref No." v={h.refNo} />
          <KV k="Co. No." v={h.coNo} />
          <KV k="Business Code" v={h.bCode} />
          <KV k="Year of Assessment" v={r.ya} />
          <KV k="Basis Period" v={h.basisFrom ? `${h.basisFrom} \u2014 ${h.basisTo || ""}` : ""} />
        </div>
      </div>

      {/* Computation */}
      <div className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-1">Tax Computation</div>
      <div className={`${CG} border-b border-slate-300 pb-1 mb-1`}>
        <div /><div />
        <div className="text-xs font-semibold text-slate-500 text-right pr-1">RM</div>
        <div className="text-xs font-semibold text-slate-500 text-right pr-1">RM</div>
      </div>

      <Row section label="Business income" />
      <Row label="Net profit / (loss) as per accounts" outer={r.netProfit} deduction={Number(r.netProfit) < 0} />
      {otherIncome.map((o, i) => <Row key={i} label={`Less: ${o.label || "Other income"}`} outer={o.amount} deduction />)}
      <Row bold subtotal label="Business income / (loss)" outer={businessIncomeLoss} deduction={businessIncomeLoss < 0} />

      <Row section label="Add: Disallowable expenses under Sec.39(1)" />
      {shownDis.map((d, i) => (
        <Row key={i} indent label={d.description} note={noteNo.get(d) ? `N${noteNo.get(d)}` : ""} inner={d.disallowed} />
      ))}
      <Row subtotal label="" outer={r.totalDisallowed} />

      {specialDeductions.length > 0 && (
        <>
          <Row section label="Less: Allowance & deductions (S.111 / S.132 / S.157)" />
          {specialDeductions.map((s, i) => <Row key={i} indent label={s.label} inner={s.amount} />)}
          <Row subtotal label="" outer={sdTotal} deduction />
        </>
      )}

      <Row bold subtotal label={r.adjustedIsLoss ? "Adjusted loss" : "Adjusted income"} outer={r.adjustedIsLoss ? r.adjustedLoss : r.adjustedIncome} deduction={r.adjustedIsLoss} />

      {(Number(r.capitalAllowanceCurrent) || Number(r.capitalAllowanceBf) || Number(r.capitalAllowanceAbsorbed) || Number(r.capitalAllowanceCf)) ? (
        <>
          <Row section label="Less: Capital allowance" />
          <Row indent label="Balance b/f" inner={r.capitalAllowanceBf} />
          <Row indent label="Current year" inner={r.capitalAllowanceCurrent} />
          <Row indent label="Amount absorbed this year" inner={r.capitalAllowanceAbsorbed} innerTop outer={r.capitalAllowanceAbsorbed} deduction />
          <Row indent label="Balance carried forward" inner={r.capitalAllowanceCf} />
        </>
      ) : null}

      <Row bold subtotal label="Statutory income" outer={r.aggregateStatutoryBusinessIncome} />
      <Row bold label="Aggregate statutory business income" outer={r.aggregateStatutoryBusinessIncome} />

      {(r.unabsorbedLossBf > 0 || r.lossBfAbsorbed > 0 || r.cyLossCf > 0 || r.unabsorbedLossCf > 0) && (
        <>
          <Row section label="Less: Unabsorbed business losses" />
          <Row indent label="Balance brought forward" inner={r.unabsorbedLossBf} />
          <Row indent label="Amount absorbed this year" inner={r.lossBfAbsorbed} innerTop outer={r.lossBfAbsorbed} deduction />
          {r.cyLossCf > 0 && <Row indent label="Balance transferred from current year loss" inner={r.cyLossCf} />}
          <Row indent label="Balance carried forward" inner={r.unabsorbedLossCf} />
        </>
      )}

      {Number(r.otherIncomeTotal) > 0 && <Row label="Add: Other statutory income" outer={r.otherIncomeTotal} />}
      <Row bold subtotal label="Aggregate income" outer={r.aggregateIncome} />

      {r.adjustedIsLoss ? (
        <>
          <Row section label="Less: Current year loss" />
          <Row indent label="Loss incurred" inner={r.adjustedLoss} />
          <Row indent label="Amount absorbed" inner={r.cyLossAbsorbed} innerTop outer={r.cyLossAbsorbed} deduction />
          <Row indent label="Balance transferred to unabsorbed losses" inner={r.cyLossCf} />
        </>
      ) : (
        r.cyLossAbsorbed > 0 && <Row label="Less: Current year loss absorbed" outer={r.cyLossAbsorbed} deduction />
      )}

      <Row emphasise label="Chargeable Income" outer={r.chargeableIncome} dataTestid="chargeable-income-value" />

      {/* Tax payable */}
      <Row section label="Tax payable" />
      {(r.taxBreakdown || []).map((b, i) => (
        <Row key={i} indent label={`Chargeable income ${b.band} @ ${b.rate}%`} inner={b.amount} />
      ))}
      <Row subtotal label="Income tax charged" outer={r.grossTax} />
      {r.section110 > 0 && <Row indent label="Less: Section 110 set-off" outer={r.section110} deduction />}
      {r.section132relief > 0 && <Row indent label="Less: Section 132/133 relief" outer={r.section132relief} deduction />}
      {r.cp204Paid > 0 && <Row indent label="Less: CP204 instalments paid" outer={r.cp204Paid} deduction />}
      <Row emphasise label={Number(r.balance) < 0 ? "Balance of tax repayable" : "Balance of tax payable"} outer={r.balance} deduction={Number(r.balance) < 0} dataTestid="tax-payable-value" />

      {/* Notes to accounts */}
      {notedRows.length > 0 && (
        <div className="mt-16">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-900 border-b border-slate-300 pb-1 mb-3">Notes to Accounts</div>
          {notedRows.map((d) => {
            const expl = (d.notes && d.notes.length) ? d.notes : (d.note ? [{ amount: d.amount, text: d.note, treatment: "disallowed" }] : []);
            let sa = 0, sd = 0;
            return (
              <div key={noteNo.get(d)} className="mb-4">
                <div className="text-xs font-bold uppercase text-slate-700 bg-slate-50 px-2 py-1 rounded-sm">Note {noteNo.get(d)} — {d.description}</div>
                <div className="grid grid-cols-[1fr_100px_100px] text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-2 pt-1">
                  <span>Explanation</span><span className="text-right">Allowed</span><span className="text-right">Disallowed</span>
                </div>
                {expl.map((e, j) => {
                  const amt = Number(e.amount || 0);
                  const allowed = e.treatment === "allowed";
                  if (allowed) sa += amt; else sd += amt;
                  return (
                    <div key={j} className="grid grid-cols-[1fr_100px_100px] text-sm px-2 py-0.5">
                      <span className="text-slate-700">{e.text}</span>
                      <span className="font-mono tabular-nums text-right">{allowed ? (amt ? fmt(amt) : "-") : "-"}</span>
                      <span className="font-mono tabular-nums text-right">{allowed ? "-" : (amt ? fmt(amt) : "-")}</span>
                    </div>
                  );
                })}
                <div className="grid grid-cols-[1fr_100px_100px] text-sm px-2 py-0.5 font-semibold border-t border-slate-300">
                  <span>Total</span>
                  <span className="font-mono tabular-nums text-right">{sa ? fmt(sa) : "-"}</span>
                  <span className="font-mono tabular-nums text-right">{sd ? fmt(sd) : "-"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Special deductions schedule */}
      {sdSchedule.length > 0 && (
        <div className="mt-16">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-900 border-b border-slate-300 pb-1 mb-3">Special Deductions Schedule (S.111 / S.132 / S.157)</div>
          {sdSchedule.map((g, i) => (
            <div key={i} className="mb-4">
              <div className="text-xs font-bold uppercase text-slate-700 bg-slate-50 px-2 py-1 rounded-sm">{g.cat.sdLabel}</div>
              {g.lines.map((l, j) => (
                <div key={j} className="grid grid-cols-[1fr_120px] text-sm px-2 py-0.5">
                  <span className="text-slate-700">{l.desc}{l.text ? ` \u2014 ${l.text}` : ""}</span>
                  <span className="font-mono tabular-nums text-right">{fmt(l.amount)}</span>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_120px] text-sm px-2 py-0.5 font-semibold border-t border-slate-300">
                <span>Total</span><span className="font-mono tabular-nums text-right">{fmt(g.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Declaration */}
      <div className="mt-16 pt-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-10">Declaration — Director / Person in charge</div>
        <div className="w-64 border-t border-slate-900 pt-1 text-xs text-slate-500">Signature &amp; Date</div>
      </div>
    </>
  );
};

export default ReportBody;
